if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error)
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

class stickyCartModal extends HTMLElement {
  connectedCallback() {
    if (this.observer) return;

    const form = document.getElementById(`product-form-${this.dataset.section}`);
    const target = form && (form.querySelector('[name="add"]') || form);
    if (!target) return;

    // The bar stands in for the main add to cart button: it shows whenever that
    // button is off screen, which on mobile includes the top of the page, where
    // it sits below the media gallery.
    this.observer = new IntersectionObserver(([entry]) => {
      this.update(!entry.isIntersecting);
    });
    this.observer.observe(target);

    document
      .querySelector('footer')
      ?.classList.add('sticky-cart-modal_bottom_padding');
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }
  update(show) {
    this.classList.toggle('show-modal', show);
    document.body.classList.toggle('modal__sticky-cart', show);
  }
}
customElements.define('sticky-cart-modal', stickyCartModal);

class stickyVariantDrawer extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    // The trigger lives inside the sticky bar, whose markup is replaced on every
    // variant change, so the click is delegated from the bar itself, which stays.
    document
      .getElementById(`sticky-cart-${this.dataset.section}`)
      ?.addEventListener('click', (event) => {
        const trigger = event.target.closest('.sticky-cart-variant-trigger');
        if (trigger) this.open(trigger);
      });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) {
        this.close();
        return;
      }

      const option = event.target.closest('.sticky-variant-drawer__option');
      if (option) this.select(option.dataset.variantId);
    });

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  disconnectedCallback() {
    document.body.classList.remove('overflow-hidden');
  }

  get variantSelect() {
    return document.querySelector(`#sticky-cart-select-${this.dataset.section} select`);
  }

  open(trigger) {
    this.trigger = trigger;
    this.markCurrent();
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    trigger.setAttribute('aria-expanded', 'true');
    trapFocus(this, this.querySelector('.sticky-variant-drawer__close'));
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.trigger?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.trigger?.isConnected ? this.trigger : null);
  }

  select(variantId) {
    const select = this.variantSelect;
    this.close();

    if (!select || !variantId || select.value === variantId) return;

    // variant-selects listens for the change and takes it from here: it swaps the
    // variant, re-renders the bar and updates the trigger label.
    select.value = variantId;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // The drawer outlives the bar's re-renders, so the selected row is read back
  // from the select every time it opens rather than tracked in the markup.
  markCurrent() {
    const selectedId = this.variantSelect?.value;

    this.querySelectorAll('.sticky-variant-drawer__option').forEach((option) => {
      if (option.dataset.variantId === selectedId) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    });
  }
}
customElements.define('sticky-variant-drawer', stickyVariantDrawer);

